import axios from 'axios';
import { TOKEN_EXPIRED } from '../hooks/useAPIHelper';

/**
 * The counts behind Home's SLA indicator, from the database (PORTAL-199), or null to mean there is no answer to show.
 * The browser no longer counts them itself: that needed the whole reactive list, which is a paged set rebuilt in full
 * every ten minutes it is in use.
 *
 * WHY IT RETURNS NULL RATHER THAN THROWING. Anything this call cannot do - the site not being set up for it, a slow
 * or failed answer, or no entitlement found for the person - is answered by returning null, and the caller leaves the
 * indicator as grey placeholders rather than showing numbers it does not have. The one exception is an expired Pronto
 * token, which every other call would also report, so it is thrown in the same words and the caller's existing
 * handling applies.
 *
 * NO ENTITLEMENT IS NOT ZERO. `entitlementRows` of 0 means the database found no contract list for the person,
 * which is a different statement from "they have no calls". Showing zeros there would look like an answer, so it
 * is returned as null.
 */

const SLA_SUMMARY_URL = '/.netlify/functions/xServiceGetReactiveCallSlaSummary';

export type SummaryPeriod = 'today' | 'thisWeek' | 'thisMonth' | 'last3Months' | 'last6Months' | 'last12Months' | 'all';

export interface SlaCounts {
  // The on-site SLA, still to come.
  upcoming: { upcoming: number; upcomingToday: number; urgentlyUpcoming: number };
  // The on-site SLA, settled.
  onsite: { missedDeadline: number; doneOnTime: number };
  // The completion SLA, settled.
  complete: { missedDeadline: number; doneOnTime: number };
}

export interface ReactiveCallSlaSummary {
  // When the data was last copied from the Extract (ISO, UTC); null where unknown.
  dataAsOfUtc: string | null;
  counts: Record<SummaryPeriod, SlaCounts>;
}

const PERIODS: SummaryPeriod[] = ['today', 'thisWeek', 'thisMonth', 'last3Months', 'last6Months', 'last12Months', 'all'];

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;

const GetReactiveCallSlaSummary = async (token: string, userName: string, customerId: string): Promise<ReactiveCallSlaSummary | null> => {
  const started = Date.now();

  try {
    const reply = await axios.post(
      SLA_SUMMARY_URL,
      `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`,
      { headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token, 'X-Pronto-Username': userName } },
    );

    const data = reply.data;

    if (!(Number(data?.entitlementRows) > 0)) {
      console.log('Reactive call SLA summary: no entitlement found for this person');
      return null;
    }

    // Every period, group and status must be a real number. A partial answer shown as the full one would put a wrong
    // count on the indicator with nothing to say so.
    for (const period of PERIODS) {
      const cell = data?.counts?.[period];

      const usable = cell
        && ['upcoming', 'upcomingToday', 'urgentlyUpcoming'].every((status) => isCount(cell.upcoming?.[status]))
        && ['missedDeadline', 'doneOnTime'].every((status) => isCount(cell.onsite?.[status]) && isCount(cell.complete?.[status]));

      if (!usable) {
        throw new Error(`the answer has no usable counts for '${period}'`);
      }
    }

    console.log(`Reactive call SLA summary: counts in ${Date.now() - started} ms`);

    return { dataAsOfUtc: data.dataAsOfUtc ?? null, counts: data.counts as Record<SummaryPeriod, SlaCounts> };
  } catch (err: any) {
    if (err?.response?.status === 401 && err.response.data === 'TokenExpired') {
      throw TOKEN_EXPIRED;
    }

    console.log('Reactive call SLA summary unavailable:', err?.message ?? err);
    return null;
  }
};

export default GetReactiveCallSlaSummary;
